import * as React from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Menu, XIcon } from 'lucide-react';
import { Dialog as DrawerPrimitive } from 'radix-ui';
import { useChatUnread } from '@/realtime/ChatUnread';
import { badgeText } from '@/lib/chat';
import { Sidebar } from './Sidebar';

/**
 * The rail below md (portal-mobile): an off-canvas drawer from the left, on the Dialog primitive, so the
 * focus trap, Esc, the scroll lock and the return of focus come from Radix rather than from hand-rolled
 * handlers. The hamburger lives in the top bar and is controlled from AppLayout.
 */

export const MOBILE_NAV_ID = 'mobile-nav';

/** Tailwind's `md` (48rem): from here the rail is on screen and the drawer must not exist. */
export const MD_QUERY = '(min-width: 48rem)';

export function MobileNavButton({
  open,
  onOpen,
  buttonRef,
}: {
  open: boolean;
  onOpen: () => void;
  buttonRef: React.Ref<HTMLButtonElement>;
}) {
  const { t } = useTranslation();
  const chat = useChatUnread();
  const unread = chat?.enabled ? chat.unreadThreads : 0;
  const text = badgeText(unread);
  return (
    <button
      ref={buttonRef}
      type="button"
      onClick={onOpen}
      aria-expanded={open}
      aria-controls={MOBILE_NAV_ID}
      aria-haspopup="dialog"
      aria-label={text ? t('layout.openMenuUnread', { count: unread }) : t('layout.openMenu')}
      className="relative -ml-2 grid size-touch shrink-0 place-items-center rounded-md text-ink-secondary hover:bg-secondary md:hidden"
    >
      <Menu className="size-6" aria-hidden="true" />
      {text && (
        <span
          aria-hidden="true"
          data-testid="chat-badge-menu"
          className="tabular-figures absolute top-0.5 right-0.5 grid min-w-5 place-items-center rounded-full bg-danger px-1 text-xs leading-5 font-semibold text-white"
        >
          {text}
        </span>
      )}
    </button>
  );
}

export function MobileNav({
  open,
  onOpenChange,
  returnFocusTo,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  returnFocusTo: React.RefObject<HTMLButtonElement | null>;
}) {
  const { t } = useTranslation();
  const { pathname } = useLocation();

  // A navigation from anywhere (a link in the drawer, the browser's back button) closes it.
  const close = React.useRef(onOpenChange);
  close.current = onOpenChange;
  React.useEffect(() => {
    close.current(false);
  }, [pathname]);

  // The drawer is only `md:hidden`: if the viewport reaches md while it is open (a phone rotated to
  // landscape, a widened window) it would vanish yet keep the page inert, scroll-locked and focus-trapped.
  // Close it for real, which releases all three.
  const desktop = React.useRef(false);
  React.useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const mql = window.matchMedia(MD_QUERY);
    desktop.current = mql.matches;
    const onChange = (e: MediaQueryListEvent) => {
      desktop.current = e.matches;
      if (e.matches) close.current(false);
    };
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);

  return (
    <DrawerPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DrawerPrimitive.Portal>
        <DrawerPrimitive.Overlay className="fixed inset-0 z-50 bg-overlay-scrim md:hidden" />
        <DrawerPrimitive.Content
          id={MOBILE_NAV_ID}
          aria-describedby={undefined}
          onCloseAutoFocus={(e) => {
            // The hamburger is not a Radix Trigger (it lives in the top bar), so hand focus back by hand.
            e.preventDefault();
            // Closed by reaching md: the hamburger is display:none there, so focus stays with the page.
            if (!desktop.current) returnFocusTo.current?.focus();
          }}
          // Tapping the page already on screen does not change the path: close on any link click.
          onClick={(e) => {
            if ((e.target as HTMLElement).closest('a[href]')) onOpenChange(false);
          }}
          className="fixed inset-y-0 left-0 z-50 flex w-72 max-w-[85vw] flex-col bg-rail shadow-overlay md:hidden"
        >
          <DrawerPrimitive.Title className="sr-only">{t('layout.menu')}</DrawerPrimitive.Title>
          <Sidebar variant="drawer" />
          <DrawerPrimitive.Close
            aria-label={t('layout.closeMenu')}
            title={t('layout.closeMenu')}
            className="absolute top-2 right-2 grid size-touch place-items-center rounded-md text-on-rail-muted hover:bg-rail-2 hover:text-on-rail"
          >
            <XIcon className="size-5" aria-hidden="true" />
          </DrawerPrimitive.Close>
        </DrawerPrimitive.Content>
      </DrawerPrimitive.Portal>
    </DrawerPrimitive.Root>
  );
}
